let totalWordsSoFar = 0;
let nextMarkerThreshold = 300;
const MARKERS_CONFIG = {
	initial: {
		gap: 300,
		added: false,
	},
	gap: 100,
	className: 'reading_marker',
};

const Tracker = {
	pageLoadTime: new Date(),
	lastTrackerEventTime: null,

	/**
	 * Get reading duration from last reading event, or the total reading time
	 * @param {boolean} getTotalReadingTime - Flag to determine if total reading time should be calculated
	 * @return {number} - Reading duration in seconds
	 */
	getReadingDuration ( getTotalReadingTime ) {
		const fromTime = getTotalReadingTime
			? this.pageLoadTime.getTime()
			: this.lastTrackerEventTime?.getTime() || this.pageLoadTime.getTime();
		return ( Date.now() - fromTime ) / 1000;
	},

	/**
	 * Send GA4 event
	 * @param {Object} options - Additional event data
	 */
	sendEvent ( options ) {
		const readingDuration = this.getReadingDuration();
		const quickScrollFlag = readingDuration > 0 && readingDuration <= 2;
		const eventData = {
			event: 'content_reading_tracker',
			timestamp: new Date(),
			quick_scroll_flag: quickScrollFlag && options.word_threshold > 0,
			word_threshold: 0,
			reading_duration: readingDuration,
			total_reading_duration: this.getReadingDuration( true ),
			...options,
		};

		dataLayer.push( eventData );

		this.lastTrackerEventTime = eventData.timestamp;
	},
};

/**
 * Enable observers for reading markers
 */
const initMarkerObservers = () => {
	const markerObserver = new IntersectionObserver( ( entries, observer ) => {
		entries.forEach( entry => {
			if ( entry.isIntersecting ) {
				// Retrieve marker's threshold (e.g., 300, 400, 500)
				const wordThreshold = parseInt( entry.target.dataset.threshold, 10 ) || 0;
				// Send GA4 event for exactly this threshold
				Tracker.sendEvent( { word_threshold: wordThreshold } );
				// Remove observer for marker once it has fired
				observer.unobserve( entry.target );
			}
		} );
	} );

	// Enable observer for all markers
	document
		.querySelectorAll( `.${MARKERS_CONFIG.className}` )
		.forEach( marker => markerObserver.observe( marker ) );
};

/**
 * processTextNode - Splits a single text into tokens, increments totalWordsSoFar,
 * and inserts <span class="reading_marker"> when crossing each threshold.
 * Returns a DocumentFragment with processed text + markers.
 * @param {string} text
 */
const processTextNode = text => {
	// Tokenize into whitespace runs (\s+) or non-whitespace (\S+)
	const tokens = text.match( /\S+|\s+/g ) || [];
	const frag = document.createDocumentFragment();

	let buffer = '';

	tokens.forEach( token => {
		if ( /\s+/.test( token ) ) {
			// If it's pure whitespace then accumulate
			buffer += token;
		} else {
			// If it's a word or punctuation then increment global count
			totalWordsSoFar ++;

			// If crossing thresholds, insert marker
			while ( totalWordsSoFar >= nextMarkerThreshold ) {
				// Empty the current buffer as a text node
				if ( buffer ) {
					frag.appendChild( document.createTextNode( buffer ) );
					buffer = '';
				}

				// Insert a marker for this threshold
				const marker = document.createElement( 'span' );
				marker.className = MARKERS_CONFIG.className;
				marker.dataset.threshold = nextMarkerThreshold;
				frag.appendChild( marker );

				if ( ! MARKERS_CONFIG.initial.added && nextMarkerThreshold === MARKERS_CONFIG.initial.gap ) {
					MARKERS_CONFIG.initial.added = true;
				}

				// For subsequent markers, we increment by 100
				nextMarkerThreshold += MARKERS_CONFIG.gap;
			}

			buffer += token; // add this word
		}
	} );

	// leftover text
	if ( buffer ) {
		frag.appendChild( document.createTextNode( buffer ) );
	}

	return frag;
};

/**
 * Process a DOM node's children:
 * - If text node, run processTextNode
 * - If element node (inline tag, etc.), clone it & repeat on its children
 * - Preserves original inline HTML structure
 *
 * Returns a DocumentFragment with processed content
 * @param {node} node
 */
function processNode ( node ) {
	const frag = document.createDocumentFragment();

	if ( node.nodeType === Node.TEXT_NODE ) {
		// Insert markers in this text
		const processed = processTextNode( node.nodeValue );
		frag.appendChild( processed );
	} else if ( node.nodeType === Node.ELEMENT_NODE ) {
		// Clone the element, then rebuild its children
		const newEl = node.cloneNode( false ); // shallow clone
		node.childNodes.forEach( child => {
			const childFrag = processNode( child );
			newEl.appendChild( childFrag );
		} );
		frag.appendChild( newEl );
	} else {
		frag.appendChild( node.cloneNode( true ) );
	}

	return frag;
}

/**
 * maybeAddReadingMarker:
 * Processes an entire <p> in place, preserving tags, inserting markers.
 * @param {HTMLElement} paragraph
 */
const maybeAddReadingMarker = paragraph => {
	// Build a new set of children by processing each child node
	const newFrag = document.createDocumentFragment();

	// Replace the original paragraph's children with our processed fragment
	for ( const child of paragraph.childNodes ) {
		newFrag.appendChild( processNode( child ) );
	}
	paragraph.replaceChildren( newFrag );
};

export const initContentReadingTracking = () => {
	// Guard against being called twice in more than one experience
	if ( window.contentReadingTracking ) {
		return;
	}
	window.contentReadingTracking = true;

	const contentContainer = document.querySelector( '.article__content' );
	if ( ! contentContainer ) {
		return;
	}

	const oldReadingMarkers = contentContainer.querySelectorAll( '.reading_marker' );
	if ( oldReadingMarkers.length ) {
		oldReadingMarkers.forEach( marker => {
			marker.remove();
		} );
	}

	totalWordsSoFar = 0;
	nextMarkerThreshold = MARKERS_CONFIG.initial.gap;

	// Select all paragraphs that are directly after the article content class. Anything deeper will be a shortcode, etc.
	const paragraphs = contentContainer.querySelectorAll( ':scope > p' );

	paragraphs.forEach( paragraph => {
		// Only count paragraphs without a class or the classic mode paragraph block. This is to not
		// count the component paragraphs as they are not part of the article word count and we do not
		// want to add a marker to the components.
		if ( paragraph.classList.contains( 'wp-block-paragraph' ) || paragraph.classList.length === 0 ) {
			maybeAddReadingMarker( paragraph );
		}
	} );

	initMarkerObservers();
	Tracker.sendEvent( {
		word_threshold: 0,
		timestamp: Tracker.pageLoadTime,
	} );
};

/**
 * Run the init when Piano returns non site action from the template
 */
window.tp = window.tp || [];
window.tp.push( [
	'addHandler',
	'nonSite',
	() => {
		initContentReadingTracking();
	},
] );

export default initContentReadingTracking;
